/**
 * 11 industries × 3 paradigms — the canonical mapping that drives the
 * Industries section (hub + detail pages).
 *
 * Industry ids match the ones already used in the home page industry
 * catalog (locale.home.industries[].id), so we can reuse their meta
 * (name / applications / coreObjects / benefit / scenario) directly.
 */

export type IndustryId =
  | 'finance'
  | 'insurance'
  | 'automotive'
  | 'retail'
  | 'healthcare'
  | 'legal'
  | 'logistics'
  | 'realestate'
  | 'energy'
  | 'education'
  | 'government'

export const INDUSTRY_IDS: readonly IndustryId[] = [
  'finance',
  'insurance',
  'automotive',
  'retail',
  'healthcare',
  'legal',
  'logistics',
  'realestate',
  'energy',
  'education',
  'government',
] as const

/**
 * Each industry's three featured paradigms. Order matters — it's the
 * order they render on the industry detail page.
 *
 * Phase F1 only ships data for Energy (re-skinned petroleum). Other
 * industries surface the slots as "coming soon" until F2/F3/F4 fills
 * them in.
 */
export const INDUSTRY_PARADIGMS: Record<IndustryId, [string, string, string]> = {
  finance: ['risk-scoring', 'question-grounded', 'change-propagation'],
  insurance: ['document-driven', 'event-driven', 'risk-scoring'],
  automotive: ['event-driven', 'change-propagation', 'what-if'],
  retail: ['change-propagation', 'what-if', 'continuous-audit'],
  healthcare: ['event-driven', 'document-driven', 'explainability'],
  legal: ['document-driven', 'change-propagation', 'question-grounded'],
  logistics: ['event-driven', 'multi-agent', 'what-if'],
  realestate: ['change-propagation', 'what-if', 'risk-scoring'],
  energy: ['event-driven', 'goal-driven', 'explainability'],
  education: ['goal-driven', 'document-driven', 'continuous-audit'],
  government: ['change-propagation', 'continuous-audit', 'multi-agent'],
}

export const DEFAULT_INDUSTRY_ID: IndustryId = 'energy'
